import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState, useMemo } from "react";
import { format, parseISO, eachDayOfInterval, addDays } from "date-fns";
import { CalendarIcon, Check, X, Trash2, LogOut, Plus, DollarSign, Save, Sparkles } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import {
  adminListBookings,
  adminUpdateBookingStatus,
  adminCreateBlock,
  adminDeleteBlock,
  listRoomsAndBlocks,
  adminUpdateRoomPrice,
  adminBulkAdjustPrices,
  adminListSeasons,
  adminCreateSeason,
  adminUpdateSeason,
  adminDeleteSeason,
} from "@/lib/booking.functions";
import { Calendar } from "@/components/ui/calendar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";

const ADMIN_EMAIL = "clients@vortio.com";

export const Route = createFileRoute("/admin/dashboard")({
  component: Dashboard,
});

function Dashboard() {
  const navigate = useNavigate();
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const check = async () => {
      const { data } = await supabase.auth.getSession();
      if (data.session?.user?.email !== ADMIN_EMAIL) {
        navigate({ to: "/admin" });
      } else {
        setReady(true);
      }
    };
    check();
    const { data: sub } = supabase.auth.onAuthStateChange((_e, session) => {
      if (session?.user?.email !== ADMIN_EMAIL) navigate({ to: "/admin" });
    });
    return () => sub.subscription.unsubscribe();
  }, [navigate]);

  if (!ready) {
    return <div className="flex min-h-screen items-center justify-center text-muted-foreground">Loading…</div>;
  }

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-10">
      <Toaster />
      <header className="flex items-center justify-between mb-8 flex-wrap gap-4">
        <div>
          <Link to="/" className="text-xs text-muted-foreground hover:text-foreground">← Back to site</Link>
          <h1 className="text-3xl font-heading text-foreground mt-1">Property Manager</h1>
          <p className="text-sm text-muted-foreground">Manage booking requests and room availability</p>
        </div>
        <button
          onClick={async () => { await supabase.auth.signOut(); navigate({ to: "/admin" }); }}
          className="inline-flex items-center gap-2 rounded-full border border-input px-4 py-2 text-sm hover:bg-accent"
        >
          <LogOut className="h-4 w-4" /> Sign out
        </button>
      </header>

      <Tabs defaultValue="requests">
        <TabsList>
          <TabsTrigger value="requests">Booking Requests</TabsTrigger>
          <TabsTrigger value="calendar">Availability Calendar</TabsTrigger>
          <TabsTrigger value="pricing">Pricing</TabsTrigger>
        </TabsList>
        <TabsContent value="requests" className="mt-6">
          <RequestsTab />
        </TabsContent>
        <TabsContent value="calendar" className="mt-6">
          <CalendarTab />
        </TabsContent>
        <TabsContent value="pricing" className="mt-6">
          <PricingTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function RequestsTab() {
  const fn = useServerFn(adminListBookings);
  const updateFn = useServerFn(adminUpdateBookingStatus);
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ["admin-bookings"],
    queryFn: () => fn(),
  });
  const update = useMutation({
    mutationFn: (vars: { id: string; status: "approved" | "declined" | "pending" }) =>
      updateFn({ data: vars }),
    onSuccess: (_d, vars) => {
      toast.success(`Booking ${vars.status}`);
      qc.invalidateQueries({ queryKey: ["admin-bookings"] });
      qc.invalidateQueries({ queryKey: ["rooms-and-blocks"] });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Failed"),
  });

  if (isLoading) return <p className="text-muted-foreground">Loading bookings…</p>;
  if (error) return <p className="text-destructive">{(error as Error).message}</p>;
  const bookings = data?.bookings ?? [];

  if (bookings.length === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-border p-12 text-center text-muted-foreground">
        No booking requests yet.
      </div>
    );
  }

  const statusColor = (s: string) =>
    s === "approved" ? "bg-primary/10 text-primary"
    : s === "declined" ? "bg-destructive/10 text-destructive"
    : "bg-muted text-foreground";

  return (
    <div className="flex flex-col gap-3">
      {bookings.map((b) => (
        <div key={b.id} className="rounded-2xl border border-border bg-card p-5 flex flex-col md:flex-row md:items-center gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-semibold text-foreground">{b.guest_name}</h3>
              <span className={cn("text-xs px-2 py-0.5 rounded-full capitalize", statusColor(b.status))}>{b.status}</span>
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              {(b.rooms as { name: string } | null)?.name ?? "Room"} · {b.guests} guest{b.guests > 1 ? "s" : ""}
            </p>
            <p className="text-sm text-foreground mt-1">
              {format(parseISO(b.check_in), "MMM d, yyyy")} → {format(parseISO(b.check_out), "MMM d, yyyy")}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              <a href={`mailto:${b.email}`} className="hover:underline">{b.email}</a>
              {b.phone && <> · <a href={`tel:${b.phone}`} className="hover:underline">{b.phone}</a></>}
            </p>
            {b.notes && <p className="text-sm text-muted-foreground mt-2 italic">"{b.notes}"</p>}
          </div>
          <div className="flex gap-2 shrink-0">
            {b.status !== "approved" && (
              <button
                onClick={() => update.mutate({ id: b.id, status: "approved" })}
                disabled={update.isPending}
                className="inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
              >
                <Check className="h-4 w-4" /> Approve
              </button>
            )}
            {b.status !== "declined" && (
              <button
                onClick={() => update.mutate({ id: b.id, status: "declined" })}
                disabled={update.isPending}
                className="inline-flex items-center gap-1.5 rounded-full border border-input px-4 py-2 text-sm hover:bg-accent"
              >
                <X className="h-4 w-4" /> Decline
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

function CalendarTab() {
  const fn = useServerFn(listRoomsAndBlocks);
  const createFn = useServerFn(adminCreateBlock);
  const deleteFn = useServerFn(adminDeleteBlock);
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["rooms-and-blocks"],
    queryFn: () => fn(),
  });

  const [selectedRoomId, setSelectedRoomId] = useState<string | null>(null);
  const [range, setRange] = useState<{ from?: Date; to?: Date } | undefined>();

  useEffect(() => {
    if (data?.rooms.length && !selectedRoomId) setSelectedRoomId(data.rooms[0].id);
  }, [data, selectedRoomId]);

  const create = useMutation({
    mutationFn: (vars: { room_id: string; start_date: string; end_date: string }) =>
      createFn({ data: vars }),
    onSuccess: () => {
      toast.success("Dates blocked");
      setRange(undefined);
      qc.invalidateQueries({ queryKey: ["rooms-and-blocks"] });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Failed"),
  });

  const remove = useMutation({
    mutationFn: (id: string) => deleteFn({ data: { id } }),
    onSuccess: () => {
      toast.success("Block removed");
      qc.invalidateQueries({ queryKey: ["rooms-and-blocks"] });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Failed"),
  });

  const blockedDays = useMemo(() => {
    if (!data || !selectedRoomId) return [] as Date[];
    const days: Date[] = [];
    for (const b of data.blocks.filter((x) => x.room_id === selectedRoomId)) {
      // end_date is exclusive (checkout day)
      const end = addDays(parseISO(b.end_date), -1);
      const start = parseISO(b.start_date);
      if (end < start) continue;
      days.push(...eachDayOfInterval({ start, end }));
    }
    return days;
  }, [data, selectedRoomId]);

  const roomBlocks = useMemo(
    () => (data?.blocks ?? []).filter((b) => b.room_id === selectedRoomId),
    [data, selectedRoomId],
  );

  if (isLoading || !data) return <p className="text-muted-foreground">Loading…</p>;

  return (
    <div className="grid lg:grid-cols-[260px_1fr] gap-6">
      <aside className="flex flex-col gap-2">
        <p className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Rooms</p>
        {data.rooms.map((r) => (
          <button
            key={r.id}
            onClick={() => { setSelectedRoomId(r.id); setRange(undefined); }}
            className={cn(
              "text-left rounded-xl border px-4 py-3 text-sm transition-colors",
              selectedRoomId === r.id
                ? "border-primary bg-primary/5 text-foreground"
                : "border-border hover:bg-accent",
            )}
          >
            <div className="font-medium">{r.name}</div>
            <div className="text-xs text-muted-foreground">C${Number(r.price_cad).toFixed(0)} / night</div>
          </button>
        ))}
      </aside>

      <div className="flex flex-col gap-6">
        <div className="rounded-2xl border border-border bg-card p-6">
          <div className="flex items-center gap-2 mb-3 text-sm text-muted-foreground">
            <CalendarIcon className="h-4 w-4" />
            Select check-in and check-out dates to mark unavailable
          </div>
          <div className="flex flex-col md:flex-row gap-6 items-start">
            <Calendar
              mode="range"
              selected={range as { from: Date; to?: Date } | undefined}
              onSelect={(r) => setRange(r as { from?: Date; to?: Date } | undefined)}
              numberOfMonths={2}
              modifiers={{ blocked: blockedDays }}
              modifiersClassNames={{ blocked: "bg-destructive/15 text-destructive line-through" }}
              disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
              className={cn("p-3 pointer-events-auto")}
            />
            <div className="flex-1 w-full">
              <div className="rounded-xl bg-muted/40 p-4 text-sm">
                <p className="font-medium text-foreground">Selection</p>
                <p className="text-muted-foreground mt-1">
                  {range?.from ? format(range.from, "MMM d, yyyy") : "Not set"} →{" "}
                  {range?.to ? format(range.to, "MMM d, yyyy") : "Not set"}
                </p>
                <button
                  disabled={!range?.from || !range?.to || !selectedRoomId || create.isPending}
                  onClick={() => {
                    if (!range?.from || !range?.to || !selectedRoomId) return;
                    create.mutate({
                      room_id: selectedRoomId,
                      start_date: format(range.from, "yyyy-MM-dd"),
                      end_date: format(range.to, "yyyy-MM-dd"),
                    });
                  }}
                  className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                >
                  <Plus className="h-4 w-4" /> Mark unavailable
                </button>
              </div>
              <p className="text-xs text-muted-foreground mt-3">
                Red dates are already blocked. Approved bookings appear here automatically.
              </p>
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-border bg-card p-6">
          <h3 className="font-semibold text-foreground mb-3">Existing blocks for this room</h3>
          {roomBlocks.length === 0 ? (
            <p className="text-sm text-muted-foreground">No blocks. All dates are available.</p>
          ) : (
            <ul className="flex flex-col divide-y divide-border">
              {roomBlocks
                .slice()
                .sort((a, b) => a.start_date.localeCompare(b.start_date))
                .map((b) => (
                  <li key={b.id} className="py-3 flex items-center justify-between gap-3">
                    <span className="text-sm text-foreground">
                      {format(parseISO(b.start_date), "MMM d, yyyy")} → {format(parseISO(b.end_date), "MMM d, yyyy")}
                    </span>
                    <button
                      onClick={() => remove.mutate(b.id)}
                      disabled={remove.isPending}
                      className="inline-flex items-center gap-1 text-xs text-destructive hover:underline"
                    >
                      <Trash2 className="h-3.5 w-3.5" /> Remove
                    </button>
                  </li>
                ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

// ============ Pricing Tab ============

function PricingTab() {
  return (
    <div className="flex flex-col gap-8">
      <RoomRatesSection />
      <BulkAdjustSection />
      <SeasonsSection />
    </div>
  );
}

function RoomRatesSection() {
  const listFn = useServerFn(listRoomsAndBlocks);
  const updateFn = useServerFn(adminUpdateRoomPrice);
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["rooms-and-blocks"],
    queryFn: () => listFn(),
  });
  const [edits, setEdits] = useState<Record<string, string>>({});

  const update = useMutation({
    mutationFn: (vars: { id: string; price_cad: number }) => updateFn({ data: vars }),
    onSuccess: () => {
      toast.success("Price updated");
      qc.invalidateQueries({ queryKey: ["rooms-and-blocks"] });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Failed"),
  });

  if (isLoading || !data) return <p className="text-muted-foreground">Loading rooms…</p>;

  return (
    <section className="rounded-2xl border border-border bg-card p-6">
      <div className="flex items-center gap-2 mb-4">
        <DollarSign className="h-5 w-5 text-primary" />
        <h2 className="text-lg font-semibold text-foreground">Room rates</h2>
      </div>
      <p className="text-sm text-muted-foreground mb-5">
        Set the base nightly rate (CAD) for each room. Seasonal adjustments below will modify what guests see during those date ranges.
      </p>
      <ul className="flex flex-col divide-y divide-border">
        {data.rooms.map((r) => {
          const current = edits[r.id] ?? String(Number(r.price_cad));
          const changed = edits[r.id] !== undefined && Number(edits[r.id]) !== Number(r.price_cad);
          return (
            <li key={r.id} className="py-3 flex flex-col sm:flex-row sm:items-center gap-3">
              <div className="flex-1 min-w-0">
                <div className="font-medium text-foreground">{r.name}</div>
                <div className="text-xs text-muted-foreground">Current base: C${Number(r.price_cad).toFixed(0)}</div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">C$</span>
                <input
                  type="number"
                  min={0}
                  step={1}
                  value={current}
                  onChange={(e) => setEdits({ ...edits, [r.id]: e.target.value })}
                  className="w-28 rounded-md border border-input bg-background px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                />
                <button
                  disabled={!changed || update.isPending}
                  onClick={() => {
                    const val = Number(edits[r.id]);
                    if (!Number.isFinite(val) || val < 0) return;
                    update.mutate({ id: r.id, price_cad: Math.round(val) });
                    setEdits((prev) => {
                      const next = { ...prev };
                      delete next[r.id];
                      return next;
                    });
                  }}
                  className="inline-flex items-center gap-1.5 rounded-full bg-primary px-3.5 py-1.5 text-xs font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                >
                  <Save className="h-3.5 w-3.5" /> Save
                </button>
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function BulkAdjustSection() {
  const adjustFn = useServerFn(adminBulkAdjustPrices);
  const qc = useQueryClient();
  const [type, setType] = useState<"percent" | "flat">("percent");
  const [value, setValue] = useState("");

  const mutate = useMutation({
    mutationFn: (vars: { type: "percent" | "flat"; value: number }) => adjustFn({ data: vars }),
    onSuccess: (res) => {
      toast.success(`Updated ${res.count} room rate${res.count === 1 ? "" : "s"}`);
      setValue("");
      qc.invalidateQueries({ queryKey: ["rooms-and-blocks"] });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Failed"),
  });

  const numericValue = Number(value);
  const valid = value !== "" && Number.isFinite(numericValue);

  return (
    <section className="rounded-2xl border border-border bg-card p-6">
      <div className="flex items-center gap-2 mb-4">
        <Sparkles className="h-5 w-5 text-primary" />
        <h2 className="text-lg font-semibold text-foreground">Bulk adjust all rooms</h2>
      </div>
      <p className="text-sm text-muted-foreground mb-5">
        Apply a one-time change to every room's base rate. Use negative values to decrease. This permanently overwrites the base prices above.
      </p>
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-end">
        <div className="flex-1">
          <label className="block text-xs uppercase tracking-wide text-muted-foreground mb-1.5">Type</label>
          <select
            value={type}
            onChange={(e) => setType(e.target.value as "percent" | "flat")}
            className="w-full sm:w-40 rounded-md border border-input bg-background px-3 py-2 text-sm"
          >
            <option value="percent">Percent (%)</option>
            <option value="flat">Flat (C$)</option>
          </select>
        </div>
        <div className="flex-1">
          <label className="block text-xs uppercase tracking-wide text-muted-foreground mb-1.5">Amount</label>
          <input
            type="number"
            step={type === "percent" ? 0.5 : 1}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={type === "percent" ? "e.g. 10 for +10%" : "e.g. 25 for +C$25"}
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
          />
        </div>
        <button
          disabled={!valid || mutate.isPending}
          onClick={() => {
            if (!valid) return;
            const label = type === "percent" ? `${numericValue}%` : `C$${numericValue}`;
            if (!confirm(`Apply ${numericValue >= 0 ? "+" : ""}${label} to every room's base price? This cannot be undone automatically.`)) return;
            mutate.mutate({ type, value: numericValue });
          }}
          className="inline-flex items-center gap-1.5 rounded-full bg-primary px-5 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
        >
          Apply to all rooms
        </button>
      </div>
    </section>
  );
}

function SeasonsSection() {
  const listFn = useServerFn(adminListSeasons);
  const createFn = useServerFn(adminCreateSeason);
  const deleteFn = useServerFn(adminDeleteSeason);
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["admin-seasons"],
    queryFn: () => listFn(),
  });

  const [form, setForm] = useState({
    name: "",
    start_date: "",
    end_date: "",
    adjustment_type: "percent" as "percent" | "flat",
    adjustment_value: "",
  });

  const create = useMutation({
    mutationFn: () =>
      createFn({
        data: {
          name: form.name.trim(),
          start_date: form.start_date,
          end_date: form.end_date,
          adjustment_type: form.adjustment_type,
          adjustment_value: Number(form.adjustment_value),
        },
      }),
    onSuccess: () => {
      toast.success("Seasonal rate added");
      setForm({ name: "", start_date: "", end_date: "", adjustment_type: "percent", adjustment_value: "" });
      qc.invalidateQueries({ queryKey: ["admin-seasons"] });
      qc.invalidateQueries({ queryKey: ["rooms-and-blocks"] });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Failed"),
  });

  const remove = useMutation({
    mutationFn: (id: string) => deleteFn({ data: { id } }),
    onSuccess: () => {
      toast.success("Removed");
      qc.invalidateQueries({ queryKey: ["admin-seasons"] });
      qc.invalidateQueries({ queryKey: ["rooms-and-blocks"] });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Failed"),
  });

  const seasons = data?.seasons ?? [];
  const valid =
    form.name.trim().length > 0 &&
    /^\d{4}-\d{2}-\d{2}$/.test(form.start_date) &&
    /^\d{4}-\d{2}-\d{2}$/.test(form.end_date) &&
    form.adjustment_value !== "" &&
    Number.isFinite(Number(form.adjustment_value));

  return (
    <section className="rounded-2xl border border-border bg-card p-6">
      <div className="flex items-center gap-2 mb-4">
        <CalendarIcon className="h-5 w-5 text-primary" />
        <h2 className="text-lg font-semibold text-foreground">Seasonal rates</h2>
      </div>
      <p className="text-sm text-muted-foreground mb-5">
        Define date ranges where prices are automatically adjusted across every room. When two seasons overlap, the most recently created one applies.
      </p>

      {/* Create */}
      <div className="rounded-xl bg-muted/40 p-4 mb-6">
        <p className="text-sm font-medium text-foreground mb-3">Add a season</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          <input
            type="text"
            placeholder="Name (e.g. Summer 2026)"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="rounded-md border border-input bg-background px-3 py-2 text-sm lg:col-span-2"
          />
          <input
            type="date"
            value={form.start_date}
            onChange={(e) => setForm({ ...form, start_date: e.target.value })}
            className="rounded-md border border-input bg-background px-3 py-2 text-sm"
          />
          <input
            type="date"
            value={form.end_date}
            onChange={(e) => setForm({ ...form, end_date: e.target.value })}
            className="rounded-md border border-input bg-background px-3 py-2 text-sm"
          />
          <div className="flex gap-2">
            <select
              value={form.adjustment_type}
              onChange={(e) => setForm({ ...form, adjustment_type: e.target.value as "percent" | "flat" })}
              className="rounded-md border border-input bg-background px-2 py-2 text-sm"
            >
              <option value="percent">%</option>
              <option value="flat">C$</option>
            </select>
            <input
              type="number"
              step={form.adjustment_type === "percent" ? 0.5 : 1}
              placeholder="Amount"
              value={form.adjustment_value}
              onChange={(e) => setForm({ ...form, adjustment_value: e.target.value })}
              className="flex-1 rounded-md border border-input bg-background px-3 py-2 text-sm"
            />
          </div>
        </div>
        <button
          disabled={!valid || create.isPending}
          onClick={() => create.mutate()}
          className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
        >
          <Plus className="h-4 w-4" /> Add season
        </button>
      </div>

      {/* List */}
      {isLoading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : seasons.length === 0 ? (
        <p className="text-sm text-muted-foreground">No seasonal rates yet. Add one above to automatically adjust prices for a date range.</p>
      ) : (
        <ul className="flex flex-col divide-y divide-border">
          {seasons.map((s) => {
            const adj = Number(s.adjustment_value);
            const label =
              s.adjustment_type === "percent"
                ? `${adj >= 0 ? "+" : ""}${adj}%`
                : `${adj >= 0 ? "+" : ""}C$${adj}`;
            return (
              <li key={s.id} className="py-3 flex items-center justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="font-medium text-foreground">{s.name}</div>
                  <div className="text-xs text-muted-foreground">
                    {format(parseISO(s.start_date), "MMM d, yyyy")} → {format(parseISO(s.end_date), "MMM d, yyyy")}
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <span
                    className={cn(
                      "text-sm font-semibold rounded-full px-3 py-1",
                      adj >= 0 ? "bg-primary/10 text-primary" : "bg-destructive/10 text-destructive",
                    )}
                  >
                    {label}
                  </span>
                  <button
                    onClick={() => {
                      if (!confirm(`Remove "${s.name}"?`)) return;
                      remove.mutate(s.id);
                    }}
                    disabled={remove.isPending}
                    className="inline-flex items-center gap-1 text-xs text-destructive hover:underline"
                  >
                    <Trash2 className="h-3.5 w-3.5" /> Remove
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
